import { createContext, useContext, useMemo, useState } from 'react'

export type SavedTripsTab = 'saved' | 'archived'

type SavedTripsTabsContextValue = {
  activeTab: SavedTripsTab
  setActiveTab: (tab: SavedTripsTab) => void
}

const SavedTripsTabsContext = createContext<SavedTripsTabsContextValue | null>(
  null,
)

/**
 * Lifts the Saved/Archived tab selection out of SavedTripsView so the view's
 * top-bar header can render the tabs, exactly like the hulk-trips
 * Vehicles/Templates tabs. The list itself lives deep inside the
 * SharedMapProvider tree, so it reads the active tab from here.
 */
export const SavedTripsTabsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [activeTab, setActiveTab] = useState<SavedTripsTab>('saved')

  const value = useMemo<SavedTripsTabsContextValue>(
    () => ({ activeTab, setActiveTab }),
    [activeTab],
  )

  return (
    <SavedTripsTabsContext.Provider value={value}>
      {children}
    </SavedTripsTabsContext.Provider>
  )
}

/**
 * Returns the tabs context, or null when rendered outside a provider (the
 * assign-trip modal renders SavedTripsView without a header, so it keeps its own
 * in-card tabs and local tab state).
 */
export const useOptionalSavedTripsTabs = () => useContext(SavedTripsTabsContext)
